import { computed, shallowRef, watch, type WatchSource } from "vue";
import { tryOnScopeDispose, useEventListener } from "@vueuse/core";

// Strato delle richieste sopra `useAuthFetch` (F0.2, ridisegno_ui.md). Dà a ogni pagina lo stesso comportamento:
// - l'ultima richiesta vince (una risposta in ritardo non sovrascrive quella più recente);
// - allo smontaggio (e al cambio rotta, che smonta la pagina) la richiesta in volo viene interrotta e nessuno stato cambia;
// - le interruzioni non sono mai errori da mostrare;
// - lo stato distingue il primo caricamento (skeleton) dall'aggiornamento silenzioso (niente skeleton) e dall'errore (ErrorState);
// - il polling opzionale non si sovrappone e si ferma a scheda nascosta.
//
//   const { dati, errore, caricamentoIniziale, ritentando, ritenta } = useRichiesta<Impianto[]>(
//     ({ authFetch }) => authFetch("/api/impianti", { method: "GET" }),
//     { intervallo: 30_000 },
//   );
//   // template: <SkeletonRegion v-if="caricamentoIniziale"/> <ErrorState v-else-if="errore && !dati" :retrying="ritentando" @retry="ritenta"/> ...
//
// `dati` resta quello dell'ultimo successo anche se un aggiornamento successivo fallisce: la pagina decide se mostrare
// l'errore (`errore && !dati` = ErrorState a tutta sezione; `errore && dati` = avviso discreto).

export type AuthFetch = <R = unknown>(url: string, opzioni?: Record<string, unknown>) => Promise<R>;

export interface Trasporto {
  authFetch: AuthFetch;
  abortRequest: (chiave: string) => void;
}

export interface ContestoRichiesta {
  // `authFetch` di `useAuthFetch` già legato a questa richiesta (interrompe la precedente, si interrompe allo smontaggio)
  authFetch: AuthFetch;
}

export interface OpzioniRichiesta {
  // esegue subito (all'avvio nel client); default true
  immediata?: boolean;
  // ms tra un aggiornamento silenzioso e il successivo (a fine richiesta, mai sovrapposti); assente = niente polling
  intervallo?: number;
  // rilancia (silenziosamente se ci sono già dati) quando cambia questa sorgente reattiva
  osserva?: WatchSource | WatchSource[];
  // solo per i test: sostituisce useAuthFetch
  trasporto?: Trasporto;
}

// Vero per le interruzioni volute (navigazione, smontaggio, nuova richiesta uguale): mai da mostrare come errore.
export function èErroreDiAnnullamento(errore: unknown): boolean {
  if (typeof errore === "string") return true;
  if (typeof errore !== "object" || errore === null) return false;
  const e = errore as { name?: unknown; cause?: unknown };
  return e.name === "AbortError" || typeof e.cause === "string" || (typeof e.cause === "object" && e.cause !== null && (e.cause as { name?: unknown }).name === "AbortError");
}

let contatoreChiavi = 0;

export function useRichiesta<T>(esegui: (contesto: ContestoRichiesta) => Promise<T>, opzioni: OpzioniRichiesta = {}) {
  const { immediata = true, intervallo, osserva } = opzioni;
  const trasporto: Trasporto = opzioni.trasporto ?? useAuthFetch();
  const chiave = `richiesta-${++contatoreChiavi}`;
  const contesto: ContestoRichiesta = {
    authFetch: (url, o) => trasporto.authFetch(url, { ...o, autoCancel: true, cancelKey: chiave }),
  };

  const dati = shallowRef<T | null>(null);
  const errore = shallowRef<Error | null>(null);
  const caricato = shallowRef(false);
  const inCorso = shallowRef(false);

  // Primo caricamento: skeleton. Ritento dopo un errore: pulsante in caricamento. Aggiornamento con dati: silenzioso.
  const caricamentoIniziale = computed(() => inCorso.value && !caricato.value && errore.value === null);
  const ritentando = computed(() => inCorso.value && errore.value !== null);
  const aggiornando = computed(() => inCorso.value && caricato.value);

  let numero = 0; // l'ultima richiesta avviata
  let distrutto = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inAttesaDiVisibilita = false;

  const nascosta = () => typeof document !== "undefined" && document.visibilityState === "hidden";

  function fermaTimer() {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }

  function programma() {
    fermaTimer();
    if (!intervallo || distrutto) return;
    timer = setTimeout(() => {
      timer = undefined;
      if (nascosta()) {
        inAttesaDiVisibilita = true; // riparte da solo quando la scheda torna visibile
        return;
      }
      void carica();
    }, intervallo);
  }

  async function carica(): Promise<void> {
    if (distrutto) return;
    const mio = ++numero;
    fermaTimer();
    inCorso.value = true;
    try {
      const risultato = await esegui(contesto);
      if (mio !== numero || distrutto) return;
      dati.value = risultato;
      caricato.value = true;
      errore.value = null;
    } catch (e) {
      if (mio !== numero || distrutto || èErroreDiAnnullamento(e)) return;
      errore.value = e instanceof Error ? e : new Error(String(e));
    } finally {
      if (mio === numero && !distrutto) {
        inCorso.value = false;
        programma();
      }
    }
  }

  if (intervallo) {
    useEventListener(typeof document === "undefined" ? undefined : document, "visibilitychange", () => {
      if (!nascosta() && inAttesaDiVisibilita && !distrutto) {
        inAttesaDiVisibilita = false;
        void carica();
      }
    });
  }

  if (osserva) watch(osserva, () => void carica());

  tryOnScopeDispose(() => {
    distrutto = true;
    fermaTimer();
    trasporto.abortRequest(chiave);
  });

  if (immediata && import.meta.client) void carica();

  return { dati, errore, caricato, caricamentoIniziale, ritentando, aggiornando, inCorso, carica, ritenta: carica };
}
